import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import Button from '../components/Button'
import { toggleAppState } from '../../actions/appState'
import { clearCart } from '../../actions/cart'
import { currentMenuSelected, menusItemsRequested, menusRequested } from '../../actions/menus'
import { clearStoredOrderUuidAndOrderNumber } from '../../actions/order'
import { KEY_CODES } from '../utils/contants'
import OfflineModal from '../OfflineModal'

import { getSelectedMenus, getIsStandAvailable, getIsMenusLoading } from '../../selectors/menus'
import { welcomeMessage } from '../utils/messages'
import { primaryNetworkConnectedWithCallbacks } from '../../utils/networkConnected'
import { getDeviceSubtype, getKioskBarcodeScanningEnabled, getSelectedMenusIds } from '../../selectors/config'
import DeviceSubtypes from '../../utils/deviceSubtypes'
import { startScanner, stopScanner, registerScannerHandlers, unRegisterScannerHandlers } from '../utils/scanner'

import ModalMessage from './ModalMessage'
import Slideshow from './Slideshow'
import { setRouteAction } from '../../VNRoutes/Actions'

import { setShouldUpdate } from '../../VNUpdateManager/ActionCreators'
import { startDataLoad } from '../../VNDataLoadManager/ActionCreators'
import { getKioskOfflineEnabled } from '../utils/config'

/**
 * AttractLoop component for displaying the attract loop screen.
 * @param {Object} props
 * @returns {JSX.Element}
 */
const AttractLoop = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const mainSectionRef = useRef(null)
  const [modalState, setModalState] = useState(null)
  const [isAttractLoopTapped, setIsAttractLoopTapped] = useState(false)
  const [isOfflineModalOpen, setIsOfflineModalOpen] = useState(false)
  const [scannedBarcode, setScannedBarcode] = useState(null)

  const currentMenuUuid = useSelector(getSelectedMenusIds)?.[0] || null
  const isStandAvailable = useSelector(getIsStandAvailable)
  const isMenusLoading = useSelector(getIsMenusLoading)
  const selectedMenus = useSelector(getSelectedMenus) || []
  const deviceSubtype = useSelector(getDeviceSubtype)
  const kioskBarcodeScanningEnabled = useSelector(getKioskBarcodeScanningEnabled)
  const kioskOfflineEnabled = getKioskOfflineEnabled({ fromAttractLoop: true })

  const setCurrentlySelectedMenuAndFetchData = () => {
    if (selectedMenus.length === 0) {
      dispatch(currentMenuSelected(currentMenuUuid))
      return
    }
    if (selectedMenus.length === 1) {
      dispatch(currentMenuSelected(selectedMenus[0].uuid))
      return
    }
    dispatch(currentMenuSelected(null))
  }

  // on component did mount
  useEffect(() => {
    dispatch(setShouldUpdate(true))

    if (!DeviceSubtypes.isKiosk(deviceSubtype)) {
      dispatch(setRouteAction('/revenue-center-setup'))
      return
    }

    // accessibility keydown listener
    document.addEventListener('keydown', keyDownHandler, false)

    dispatch(clearCart())
    dispatch(clearStoredOrderUuidAndOrderNumber())
    setCurrentlySelectedMenuAndFetchData()

    document.title = 'Ordering Kiosk'

    dispatch(
      toggleAppState({
        isAgeConfirmed: false,
        isAlcoholWarningConfirmed: false,
      }),
    )

    setTimeout(() => mainSectionRef?.current?.focus(), 10)

    if (kioskBarcodeScanningEnabled) {
      startScanner()
      registerScannerHandlers(
        (data) => goActive(data),
        (err) => {},
      )
    }

    // cleanup function at dismount
    return () => {
      document.removeEventListener('keydown', keyDownHandler, false)

      if (kioskBarcodeScanningEnabled) {
        stopScanner()
        unRegisterScannerHandlers()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const isMenusReloaded = isMenusLoading === false || kioskOfflineEnabled
    const multipleMenusAvailable = selectedMenus.length > 1

    if (isMenusReloaded && isAttractLoopTapped) {
      dispatch(setShouldUpdate(false))

      const nextStep = () => {
        //when the user taps the attract loop we want to make sure we check on refresh data and we ALWAYS fetch menus
        dispatch(startDataLoad())
        dispatch(menusRequested())
        dispatch(menusItemsRequested())

        if (isStandAvailable) {
          dispatch(toggleAppState({ isActive: true }))
          if (scannedBarcode) {
            history.push('/kiosk/order/new', { scannedBarcode })
            return
          }
          if (multipleMenusAvailable) {
            history.push('/kiosk/menu_select')
            return
          }
          history.push('/kiosk/order/new')
          return
        }

        setModalState(null)
        setScannedBarcode(null)

        if (!selectedMenus.length) {
          setModalState('There is no available menu.')
          return
        }

        setModalState('This location is not currently accepting orders.')
      }

      if (kioskOfflineEnabled) {
        nextStep()
        setIsAttractLoopTapped(false)
        return
      }

      primaryNetworkConnectedWithCallbacks(nextStep, () => {
        setModalState(null)
        setScannedBarcode(null)
      })

      setIsAttractLoopTapped(false)
    }
  }, [
    history,
    isAttractLoopTapped,
    isMenusLoading,
    isStandAvailable,
    scannedBarcode,
    selectedMenus.length,
    dispatch,
    kioskOfflineEnabled,
  ])

  const goActive = (scannedBarcode = null) => {
    if (isOfflineModalOpen) return
    setIsAttractLoopTapped(true)
    setScannedBarcode(scannedBarcode)
  }

  // Handle key down events for accessibility and kiosk functions
  const keyDownHandler = (event) => {
    const accessibilityKeyCodes = [KEY_CODES.ENTER, KEY_CODES.UP, KEY_CODES.DOWN, KEY_CODES.LEFT, KEY_CODES.RIGHT]
    if (accessibilityKeyCodes.includes(event.keyCode)) goActive()

    if (event.keyCode === KEY_CODES.JACK_IN) {
      mainSectionRef.current?.focus()
    }
  }

  return (
    <>
      {!kioskOfflineEnabled && (
        <OfflineModal id="offlineModal" intervalTimer={1000} intervalTimeout={900} onRenderChange={setIsOfflineModalOpen} />
      )}
      <div className="attract-loop" tabIndex="0" aria-label={welcomeMessage} ref={mainSectionRef}>
        <Button className="attract-loop__btn" onClick={() => goActive()} />
        <ModalMessage state={!isOfflineModalOpen ? modalState : null} closeModal={() => setModalState(null)} />
        <Slideshow />
      </div>
    </>
  )
}

export default AttractLoop
